/**
 * Browser utility to generate a JPEG thumbnail Blob from a video file or blob using HTML5 <video> and <canvas>.
 * Returns null if thumbnail creation fails (e.g. unsupported browser codec or error),
 * allowing backend fallbacks to trigger seamlessly.
 */
export async function generateVideoThumbnailBlob(
  file: File | Blob,
  targetWidth = 200,
  targetHeight = 200,
  quality = 0.85
): Promise<Blob | null> {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return null
  }

  const mimeType = file.type || ''
  const filename = (file as File).name || ''
  if (mimeType && !mimeType.startsWith('video/') && !/\.(mp4|mov|webm|m4v)$/i.test(filename)) {
    return null
  }

  return new Promise((resolve) => {
    let objectUrl: string | null = null
    let timeoutId: ReturnType<typeof setTimeout> | null = null

    const cleanup = () => {
      if (timeoutId) clearTimeout(timeoutId)
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }

    try {
      objectUrl = URL.createObjectURL(file)
      const video = document.createElement('video')
      video.muted = true
      video.playsInline = true

      // Timeout safety (6 seconds)
      timeoutId = setTimeout(() => {
        console.warn('[VideoThumbnailGenerator] Thumbnail extraction timed out')
        cleanup()
        resolve(null)
      }, 6000)

      const captureFrame = () => {
        try {
          const canvas = document.createElement('canvas')
          canvas.width = targetWidth
          canvas.height = targetHeight
          const ctx = canvas.getContext('2d')

          if (!ctx || video.videoWidth === 0 || video.videoHeight === 0) {
            cleanup()
            resolve(null)
            return
          }

          // Calculate scaling to center and crop maintaining aspect ratio
          const videoAspect = video.videoWidth / video.videoHeight
          const targetAspect = targetWidth / targetHeight
          let renderWidth = targetWidth
          let renderHeight = targetHeight
          let offsetX = 0
          let offsetY = 0

          if (videoAspect > targetAspect) {
            renderWidth = targetHeight * videoAspect
            offsetX = -(renderWidth - targetWidth) / 2
          } else {
            renderHeight = targetWidth / videoAspect
            offsetY = -(renderHeight - targetHeight) / 2
          }

          ctx.drawImage(video, offsetX, offsetY, renderWidth, renderHeight)

          canvas.toBlob(
            (blob) => {
              cleanup()
              resolve(blob)
            },
            'image/jpeg',
            quality
          )
        } catch (err) {
          console.warn('[VideoThumbnailGenerator] Canvas capture failed:', err)
          cleanup()
          resolve(null)
        }
      }

      video.onloadeddata = () => {
        const duration = video.duration || 1
        const seekTime = Math.min(1.0, Math.max(0.1, duration * 0.1))
        video.currentTime = seekTime
      }

      video.onseeked = () => {
        captureFrame()
      }

      video.onerror = (e) => {
        console.warn('[VideoThumbnailGenerator] Video load error:', e)
        cleanup()
        resolve(null)
      }

      video.src = objectUrl
      video.load()
    } catch (err) {
      console.warn('[VideoThumbnailGenerator] Unexpected error:', err)
      cleanup()
      resolve(null)
    }
  })
}
